import { ActionButton, ActionButtonType, Callout } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ChannelResource, EnvironmentV2Resource, GetTeamsLookupBffResponseTeam, GitRefResource, PackageReference, ProcessTemplateParameterResource, ProcessTemplateResource, ProjectSummaryResource, PropertyValueResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { isAccountControlType, ControlType, getParametersOfType, ProcessType } from "@octopusdeploy/octopus-server-client";
import { flatten } from "lodash";
import { useMemo, useState } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import type { ParameterValueRow } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ProcessTemplateParametersTable } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateParametersTable";
import type { StoredAction } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useOptionalProjectContext } from "~/areas/projects/context";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { ProjectSourceItems, ProjectTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { FormSection, FormSectionHeading } from "~/components/form/index";
import { getAllProjectActionTemplates } from "~/utils/LoadProjectVariables/loadProjectVariables";
import { stringifyActionProperty } from "~/utils/actionPropertyUtils";
import { useOptionalProducerProjectTemplateContext } from "../ProjectTemplates/ProducerProjectTemplateContext";
import { defaultScopeValues, getAllVariables, partitionParameterValueRowsByAccountType, shouldRenderParameter, templateParameterValueStyles } from "./TemplateParameterHelpers";
interface ConsumerProcessTemplateParametersProps {
    stepId: string;
    action: StoredAction;
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    processTemplate: ProcessTemplateResource;
    gitRefResource: GitRefResource | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
interface ClearOldParameterValuesCalloutProps {
    onClearValues: () => void;
}
export function ClearOldParameterValuesCallout({ onClearValues }: ClearOldParameterValuesCalloutProps) {
    return (<Callout type={"information"} title={"Clear old parameter values"}>
            <div className={styles.calloutContainer}>
                <div>One or more parameters have been removed from the current version of the process template. We recommend clearing these values to avoid any unexpected behavior.</div>
                <div>
                    <ActionButton type={ActionButtonType.Primary} label={"Clear Removed Values"} onClick={onClearValues}/>
                </div>
            </div>
        </Callout>);
}
export function ConsumerProcessTemplateParametersTab({ stepId, action, doBusyTask, onParameterValueChanged, processTemplate, gitRefResource, processVariableSet, libraryVariableSets, environments, availableTargetTags, analyticsStepEditorDispatch, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ConsumerProcessTemplateParametersProps) {
    const projectContext = useOptionalProjectContext();
    const processContext = useProcessContext();
    const actionNames: string[] = flatten(Object.values(processContext.state.model.actions.byId).map((a) => a.Name));
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const [parameterWorkerPools, setParameterWorkerPools] = useState(availableWorkerPools);
    const platformHubAccountIds = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const ids = processTemplate.Parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value as string));
        return Array.from(new Set(ids)); //return distinct ids
    }, [processTemplate.Parameters]);
    const { result: platformHubAccountNames, error: platformHubAccountNamesError, isLoading, } = useInlineStatusQuery(() => repository.PlatformHubAccounts.getAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const packageReferences: PackageReference[] = getParametersOfType(processTemplate.Parameters, ControlType.Package).map((packageParam) => {
        const existingPackage = action.Packages.find((pkg) => packageParam.Name === pkg.Name);
        return {
            Name: packageParam.Name,
            PackageId: "",
            FeedId: "",
            AcquisitionLocation: "",
            Properties: {
                PackageParameterName: packageParam.Name,
                SelectionMode: "deferred",
            },
            Id: "",
            Version: existingPackage?.Version,
        };
    });
    const projectTemplateContext = useOptionalProducerProjectTemplateContext();
    // When consuming process templates within project templates there is no project context.
    const projectId = projectContext?.state.model.Id ?? "";
    let actionTemplateParameters: ActionTemplateParameterResource[] = [];
    if (projectContext) {
        actionTemplateParameters = getAllProjectActionTemplates(projectContext.state.model, isFailedPermissionCheck(libraryVariableSets) ? [] : libraryVariableSets.map((lvs) => lvs.libraryVariableSet));
    }
    else if (projectTemplateContext) {
        // For project templates, use the project template parameters as action template parameters
        // This allows them to be selected in variable/parameter selectors
        actionTemplateParameters = projectTemplateContext.parameters;
    }
    const processIdentifier = processContext.state.processIdentifier;
    const baseSourceItems = {
        stepNames: actionNames,
        projectId,
        processIdentifier: processContext.state.processIdentifier,
        variableSet: processVariableSet,
        libraryVariableSets,
        refreshVariables,
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            setPackages: (packages: PackageReference[]) => {
                processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, Packages: packages }));
            },
        },
        availableTargetTags,
        environments,
        workerPools: {
            items: parameterWorkerPools,
            onRequestRefresh: () => doBusyTask(async () => {
                const summaries = await repository.WorkerPools.summary();
                setParameterWorkerPools(summaries.WorkerPoolSummaries.map((s) => s.WorkerPool));
            }),
        },
        teams,
        channels,
        projects,
        actionTemplateParameters,
    };
    const projectTemplateSlug = processIdentifier.type === ProcessType.ProjectTemplate ? processIdentifier.slug : processIdentifier.type === ProcessType.TemplatedProject ? processIdentifier.projectTemplateSlug : undefined;
    const sourceItems: ProjectSourceItems | ProjectTemplateSourceItems = projectTemplateSlug ? { ...baseSourceItems, projectTemplateSlug } : { ...baseSourceItems, projectId };
    const parameterNamesInAction = Object.keys(action.Properties).filter((p) => !p.startsWith("Octopus"));
    const parameterNamesInProcessTemplate = new Set(processTemplate.Parameters.map((p) => p.Name));
    const parametersRemovedFromProcessTemplate = parameterNamesInAction.filter((p) => !parameterNamesInProcessTemplate.has(p));
    const isProjectTemplate = !!projectTemplateSlug;
    const visibleParameters = processTemplate.Parameters.filter((p) => shouldRenderParameter(p, action.Properties[p.Name], availableWorkerPools, isTenanted, isProjectTemplate));
    const parameterValueRows = getParameterValueRows(visibleParameters, action, sourceItems);
    const sortedParameterValueRows = partitionParameterValueRowsByAccountType(parameterValueRows);
    const clearValues = () => {
        processContext.actions.removeActionProperties(action.Id, parametersRemovedFromProcessTemplate);
    };
    if (platformHubAccountNamesError) {
        return (<Callout type="danger" title="Process template account parameter issue">
                The Platform Hub account referenced by a template parameter is no longer available. Contact your Platform Hub administrator to resolve this issue.
            </Callout>);
    }
    if (visibleParameters.length === 0 || isLoading || !platformHubAccountNames) {
        return null;
    }
    return (<>
            {parametersRemovedFromProcessTemplate.length > 0 && <ClearOldParameterValuesCallout onClearValues={clearValues}/>}
            <ProcessTemplateParametersTable parameterValueRows={sortedParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch}/>
            {parametersRemovedFromProcessTemplate.length > 0 && (<>
                    <FormSectionHeading title="Old Parameter Values"/>
                    {parametersRemovedFromProcessTemplate.map((parameter) => (<FormSection key={parameter} title={<span className={styles.removedParameter}>{parameter}</span>} includeBorder={true} help={<span className={styles.removedParameter}>{stringifyActionProperty(action.Properties[parameter])}</span>}/>))}
                </>)}
        </>);
}
function getParameterValueRows(parameters: ProcessTemplateParameterResource[], action: StoredAction, sourceItems: ProjectSourceItems | ProjectTemplateSourceItems): ParameterValueRow[] {
    return parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        const controlType = p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        const overrideVariableName = action.Properties[p.Name];
        if (p.Values.length === 0 && !(overrideVariableName && typeof overrideVariableName === "string" && isAccountControlType(controlType))) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        if (overrideVariableName && typeof overrideVariableName === "string" && isAccountControlType(controlType)) {
            const completeOverrideVariableEnvironmentScope = getAllVariables(sourceItems)
                .filter((variable) => variable.Name === overrideVariableName)
                .flatMap((variable) => variable.Scope?.Environment?.filter((e) => e !== undefined) ?? []);
            const parameterWithOverrides = {
                ...p,
                Values: p.Values.map((v) => ({
                    ...v,
                    OverriddenScope: { Environment: completeOverrideVariableEnvironmentScope },
                    OverrideValue: overrideVariableName,
                })),
            };
            const overrideVariableRow: ParameterValueRow = {
                Parameter: p,
                Value: { Value: overrideVariableName, Scope: {}, OverriddenScope: {} },
                ParameterIndex: parameterIndex,
                ValueIndex: 0,
            };
            return [overrideVariableRow, ...parameterWithOverrides.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: parameterWithOverrides, Value: v, ParameterIndex: parameterIndex, ValueIndex: 1 + valueIndex }))];
        }
        return p.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: p, Value: v, ParameterIndex: parameterIndex, ValueIndex: valueIndex }));
    });
}
const styles = templateParameterValueStyles;
